import { renderToStaticMarkup } from "react-dom/server";
import { marked } from "marked";

// Configure marked for table parsing only
marked.use({
    gfm: true,  // GitHub Flavored Markdown (includes tables)
    breaks: false
});

export type AlertLevel = "critical" | "medium" | "high";

export type AlertMarker = {
    position: number;
    level: AlertLevel;
    title: string;
    items: Array<{ icon?: string; text: string }>;
    recommendation?: string;
    vehicleName?: string;  // For critical alerts with vehicle context
    vehiclePrice?: string; // For critical alerts with vehicle context
};

type HtmlParsedAnswer = {
    answerHtml: string;
    citations: string[];
    imageMarkers: Array<{
        position: number;
        carId: string;
        imageUrl: string;
        altText: string;
        vehicleData?: {
            marca?: string;
            modelo?: string;
            fecha_fabricacion?: string;
            kilometraje?: string;
            departamento?: string;
            precio?: number;
            precio_mercado_promedio?: number;
            precio_mercado_maximo?: number;
        };
    }>;
    alertMarkers: AlertMarker[];
    hasPanelLink: boolean;
};

export function parseAnswerToHtml(
    answer: string,
    isStreaming: boolean,
    onCitationClicked: (citationFilePath: string) => void,
    dataPoints?: { [key: string]: any }
): HtmlParsedAnswer {
    const citations: string[] = [];
    const imageMarkers: Array<{
        position: number;
        carId: string;
        imageUrl: string;
        altText: string;
        vehicleData?: {
            marca?: string;
            modelo?: string;
            fecha_fabricacion?: string;
            kilometraje?: string;
            departamento?: string;
            precio?: number;
            precio_mercado_promedio?: number;
            precio_mercado_maximo?: number;
        };
    }> = [];
    const alertMarkers: AlertMarker[] = [];

    // trim any whitespace from the end of the answer after removing follow-up questions
    let parsedAnswer = answer.trim();

    // Extract alert blocks and replace with placeholder divs
    // Format: [ALERT:level:title]item1|item2|item3|RECOMENDACION:text[/ALERT]
    // Extended format for critical with vehicle context: [ALERT:critical:title:vehicleName:vehiclePrice]
    let alertIndex = 0;
    parsedAnswer = parsedAnswer.replace(
        /\[ALERT:(critical|medium|high):([^\]\n]+)\]([\s\S]*?)\[\/ALERT\]/g,
        (match, level, titlePart, content) => {
            const lines = content.trim().split("\n").filter((line: string) => line.trim());
            const items: Array<{ icon?: string; text: string }> = [];
            let recommendation: string | undefined;
            let vehicleName: string | undefined;
            let vehiclePrice: string | undefined;

            // Parse extended title format for critical alerts: title:vehicleName:vehiclePrice
            let title = titlePart;
            if (level === "critical") {
                const titleParts = titlePart.split(":");
                if (titleParts.length >= 2) {
                    title = titleParts[0].trim();
                    vehicleName = titleParts[1]?.trim();
                    vehiclePrice = titleParts[2]?.trim();
                }
            }

            for (const line of lines) {
                const trimmedLine = line.trim();
                if (trimmedLine.startsWith("RECOMENDACION:") || trimmedLine.startsWith("RECOMENDACIÓN:")) {
                    recommendation = trimmedLine.replace(/^RECOMENDACI[OÓ]N:\s*/, "");
                } else if (trimmedLine) {
                    // Check if line starts with an emoji (expanded set including common alert emojis)
                    const emojiMatch = trimmedLine.match(/^([\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F000}-\u{1F02F}]|[\u{1F0A0}-\u{1F0FF}]|[❌✅⚠️📸📝🔍💡👉🚗🚨💰📊🌊🎨⛔🛡️])\s*/u);
                    if (emojiMatch) {
                        items.push({
                            icon: emojiMatch[1],
                            text: trimmedLine.slice(emojiMatch[0].length)
                        });
                    } else {
                        items.push({ text: trimmedLine });
                    }
                }
            }

            alertMarkers.push({
                position: alertIndex,
                level: level as AlertLevel,
                title,
                items,
                recommendation,
                vehicleName,
                vehiclePrice
            });

            const placeholder = `<div class="alert-box-placeholder" data-alert-index="${alertIndex}"></div>`;
            alertIndex++;
            return placeholder;
        }
    );

    // Extract image markers and replace with placeholder divs
    // This allows React to manage images separately from dangerouslySetInnerHTML
    if (dataPoints) {
        let imageIndex = 0;
        parsedAnswer = parsedAnswer.replace(/\[IMAGE:([^\]]+)\]/g, (match, carId, offset) => {
            const carData = dataPoints[carId];
            if (carData && carData.imagen_url && carData.imagen_url.trim()) {
                const imageUrl = carData.imagen_url;
                const altText = `${carData.marca || ''} ${carData.modelo || ''} ${carData.fecha_fabricacion || ''}`.trim();

                // Store image data for React rendering
                imageMarkers.push({
                    position: imageIndex,
                    carId,
                    imageUrl,
                    altText,
                    vehicleData: {
                        marca: carData.marca,
                        modelo: carData.modelo,
                        fecha_fabricacion: carData.fecha_fabricacion,
                        kilometraje: carData.kilometraje,
                        departamento: carData.departamento,
                        precio: carData.precio,
                        precio_mercado_promedio: carData.precio_mercado_promedio,
                        precio_mercado_maximo: carData.precio_mercado_maximo
                    }
                });

                // Return a placeholder div with unique ID
                const placeholder = `<div class="car-image-placeholder" data-image-index="${imageIndex}"></div>`;
                imageIndex++;
                return placeholder;
            }
            return '';
        });
    }

    // Parse markdown links first [text](url) -> <a href="url" target="_blank">text</a>
    parsedAnswer = parsedAnswer.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, text, url) => {
        // Ensure the URL is properly encoded and safe
        const safeUrl = url.replace(/"/g, '&quot;');
        return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${text}</a>`;
    });

    // Parse bold text **text** -> <strong>text</strong>
    parsedAnswer = parsedAnswer.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Parse markdown tables using marked library for proper formatting
    // This regex matches markdown table blocks (header + separator + rows)
    const tableRegex = /(\|[^\n]+\|\r?\n\|[-:| ]+\|\r?\n(?:\|[^\n]+\|\r?\n?)*)/g;
    parsedAnswer = parsedAnswer.replace(tableRegex, (tableMatch) => {
        try {
            // Use marked to parse the table into proper HTML
            const tableHtml = marked.parse(tableMatch.trim()) as string;
            return tableHtml;
        } catch (e) {
            // If parsing fails, return original text
            return tableMatch;
        }
    });

    // Parse panel link - replace "Detalles completos en el panel derecho →" or similar with clickable button
    // Match variations: "👉 Detalles completos en el panel derecho →", "👉 Detalles en el panel derecho →"
    let hasPanelLink = false;
    const panelLinkRegex = /👉\s*Detalles\s*(completos\s*)?en\s*el\s*panel\s*derecho\s*→?/gi;
    const buttonHtml = '<button class="panel-link-button" data-panel-link="true"><span class="panel-link-icon">📋</span><span class="panel-link-text">Ver detalles</span><span class="panel-link-arrow">→</span></button>';
    const newParsedAnswer = parsedAnswer.replace(panelLinkRegex, buttonHtml);
    if (newParsedAnswer !== parsedAnswer) {
        hasPanelLink = true;
        parsedAnswer = newParsedAnswer;
    }

    // Omit a citation that is still being typed during streaming
    if (isStreaming) {
        let lastIndex = parsedAnswer.length;
        for (let i = parsedAnswer.length - 1; i >= 0; i--) {
            if (parsedAnswer[i] === "]") {
                break;
            } else if (parsedAnswer[i] === "[") {
                lastIndex = i;
                break;
            }
        }
        const truncatedAnswer = parsedAnswer.substring(0, lastIndex);
        parsedAnswer = truncatedAnswer;
    }

    const parts = parsedAnswer.split(/\[([^\]]+)\]/g);

    const fragments: string[] = parts.map((part, index) => {
        if (index % 2 === 0) {
            return part;
        } else {
            // Still track citations but don't render the superscript links
            if (citations.indexOf(part) === -1) {
                citations.push(part);
            }
            return "";
        }
    });

    return {
        answerHtml: fragments.join(""),
        citations,
        imageMarkers,
        alertMarkers,
        hasPanelLink
    };
}
